import { FC } from 'react';
import {
    FormWithRedirect,
    SaveButton,
    DeleteButton,
    TextInput,
    AutocompleteInput,
    ReferenceInput,
    SelectInput,
    required,
    email,
    useTranslate,
    FormDataConsumer,
    useInput,
    useRecordContext,
    useQueryWithStore,
    // regex
} from 'react-admin';
import {
    makeStyles,
    createStyles,
    Theme,
    Typography,
    Box,
    Toolbar,
    Grid,
    AccordionSummary,
    AccordionDetails,
    useMediaQuery,
} from '@material-ui/core';
import ChipInputArray from 'material-ui-chip-input'
import get from 'lodash/get';

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useFormState } from 'react-final-form';

import getIndustryTypes from './industryTypes'
import getAccountTypes from '../utils/account-types';
import getCountries from '../utils/countries';
import getPersonTypes from '../utils/person-types'
import { SemiControlledAccordion } from '../components/Accordion';


const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        fixed_fee: {},
        amount: { fontWeight: 'bold' },
        root: {
            flexGrow: 1,
        },
        paper: {
            padding: theme.spacing(2),
            textAlign: 'center',
            color: theme.palette.text.secondary,
        },
    }),
);

const BrandIdMultipleInput = (props: any) => {
    const {
        input: { onChange }, //name, ...rest
        meta: { touched, error },
        isRequired
    } = useInput(props)
    const translate = useTranslate()
    const record = useRecordContext(props);
    const value = get(record, props.source!);

    return <ChipInputArray
        defaultValue={value}
        label={props.label ? translate(props.label) : translate(`resources.${props.resource}.fields.${props.source}`)}
        onChange={onChange}
        error={!!(touched && error)}
        helperText={touched && error}
        required={isRequired}
        {...props}
    />
}


const BankInput = (props: any) => {
    const { values } = useFormState();

    const country = values.metadata?.country

    const { loaded, data } = useQueryWithStore({
        type: 'getList',
        resource: 'bank',
        payload: { filter: { country } }
    });

    const {
        input: { onChange },
    } = useInput({ ...props, source: props.source_name });

    return <AutocompleteInput
        choices={data || [{ code: 0, name: 'UNKNOWN' }]}
        optionText="name"
        optionValue="code"
        loaded={loaded}
        onSelect={(e) => {
            onChange(e.name)
        }}
        {...props}
    />
}


const MerchantCommon: FC<{}> = (props: any) => {
    const classes = useStyles()
    const translate = useTranslate()

    const industryTypes = getIndustryTypes()
    const countries = getCountries()
    const accountTypes = getAccountTypes()
    const personTypes = getPersonTypes()

    const resource = props.resource

    const extendBankInfo = ['Ecuador', 'Colombia']

    const isMedium = useMediaQuery<Theme>(theme =>
        theme.breakpoints.down('md')
    );

    return <FormWithRedirect
        {...props}
        render={formProps => {
            // console.log(formProps.record)
            return (
                // here starts the custom form layout
                <>
                    <Typography variant="h6">{translate("resources.common.fieldGroups.basicInfo")}</Typography>
                    <Grid container className={classes.root} spacing={2}>
                        <Grid item xs={12} lg={6}>
                            <TextInput source="name" resource={resource} validate={[required()]} fullWidth />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <SelectInput source="metadata.country" validate={[required()]} choices={countries} resource={resource} fullWidth />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <AutocompleteInput source="industry" choices={industryTypes} resource={resource} fullWidth />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={4}>
                            <ReferenceInput
                                source="financialRegimenId"
                                reference="FinancialRegimen"
                                resource={resource}
                                sort={{ field: 'description', order: 'ASC' }}
                                perPage={10}
                                filterToQuery={description => ({ description })}
                            >
                                <SelectInput optionText="description" fullWidth validate={[required()]} />
                            </ReferenceInput>
                        </Grid>
                        <Grid item xs={12} sm={6} lg={5}>
                            <TextInput source="metadata.email" type="email" validate={[required(), email()]} resource={resource} fullWidth />
                        </Grid>
                        <Grid item xs={12} sm={3} lg={3}>
                            <TextInput source="metadata.phone" type="tel" validate={[]} resource={resource} fullWidth />
                        </Grid>
                    </Grid>
                    <SemiControlledAccordion defaultOpen={!isMedium}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="other-info-panel"
                            id="other-info-panel"
                        >
                            <Typography variant="h6">{translate("resources.common.fieldGroups.accountInfo")}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container className={classes.root} spacing={2}>
                                <Grid item xs={12} sm={6} lg={6}>
                                    <TextInput source="metadata.legal_name" type="text" resource={resource} fullWidth />
                                </Grid>
                                <Grid item xs={12} sm={6} lg={3}>
                                    <SelectInput source="metadata.person_type" choices={personTypes} resource={resource} fullWidth />
                                </Grid>

                                <Grid item xs={12} sm={6} lg={3}>
                                    <TextInput source="metadata.account" type="text" validate={[]} resource={resource} fullWidth />
                                </Grid>

                                <Grid item xs={12} sm={6} lg={3}>
                                    <BankInput source="metadata.bank_id" source_name="metadata.bank" resource={resource} fullWidth />
                                </Grid>
                                <FormDataConsumer>
                                    {({ formData, ...rest }) => extendBankInfo.includes(formData.metadata?.country) &&
                                        <>
                                            <Grid item xs={12} sm={6} lg={3}>
                                                <TextInput source="metadata.beneficiary_account" type="text" validate={[]} resource={resource} fullWidth />
                                            </Grid>
                                            <Grid item xs={12} sm={6} lg={3}>
                                                <SelectInput source="metadata.account_type" choices={accountTypes} resource={resource} fullWidth />
                                            </Grid>
                                        </>
                                    }
                                </FormDataConsumer>
                                <FormDataConsumer>
                                    {({ formData, ...rest }) => formData.metadata?.country === 'Ecuador' &&
                                        <>
                                            <Grid item xs={12} sm={6} lg={3}>
                                                <TextInput source="metadata.ruc" type="text" validate={[]} resource={resource} fullWidth />
                                            </Grid>
                                            <Grid item xs={12} sm={6} lg={3}>
                                                <TextInput source="metadata.ecuador_id" type="text" validate={[]} resource={resource} fullWidth />
                                            </Grid>
                                        </>
                                    }
                                </FormDataConsumer>
                                <FormDataConsumer>
                                    {({ formData, ...rest }) => formData.metadata?.country === 'Mexico' &&
                                        <>
                                            <Grid item xs={12} sm={6} lg={3}>
                                                <TextInput source="metadata.clabe" type="text" validate={[]} resource={resource} fullWidth />
                                            </Grid>
                                            <Grid item xs={12} sm={6} lg={3}>
                                                <TextInput source="metadata.curp" type="text" validate={[]} resource={resource} fullWidth />
                                            </Grid>
                                            <Grid item xs={12} sm={6} lg={3}>
                                                <TextInput source="metadata.clave_elector" type="text" validate={[]} resource={resource} fullWidth />
                                            </Grid>
                                            <Grid item xs={12} sm={6} lg={3}>
                                                <TextInput source="metadata.passport" type="text" validate={[]} resource={resource} fullWidth />
                                            </Grid>
                                            <Grid item xs={12} sm={6} lg={3}>
                                                <TextInput source="metadata.rfc" type="text" validate={[]} resource={resource} fullWidth />
                                            </Grid>
                                        </>
                                    }
                                </FormDataConsumer>
                                <Grid item xs={12} sm={6} lg={6}>
                                    <TextInput source="metadata.residence" type="text" validate={[]} resource={resource} fullWidth />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </SemiControlledAccordion>
                    <SemiControlledAccordion defaultOpen={!isMedium}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="operative-info-panel"
                            id="operative-info-panel"
                        >
                            <Typography variant="h6">{translate("resources.common.fieldGroups.operativeInfo")}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container className={classes.root} spacing={2}>
                                <Grid item xs={12} sm={6} lg={2}>
                                    <TextInput source="metadata.srpago_id" type="text" validate={[]} resource={resource} fullWidth />
                                </Grid>
                                <Grid item xs={12} sm={6} lg={2}>
                                    <TextInput source="metadata.pideaky_id" type="text" validate={[]} resource={resource} fullWidth />
                                </Grid>
                                <Grid item xs={12} sm={6} lg={2}>
                                    <TextInput source="metadata.clip_id" type="text" validate={[]} resource={resource} fullWidth />
                                </Grid>
                                <Grid item xs={12} sm={6} lg={6}>
                                    {/* <TextInput source="metadata.rappi_id" type="text" validate={[]} resource={resource} fullWidth /> */}
                                    <BrandIdMultipleInput source="metadata.rappi.brand_ids" resource={resource} fullWidth />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </SemiControlledAccordion>
                    <Toolbar>
                        <Box display="flex" justifyContent="space-between" width="100%">
                            <SaveButton
                                saving={formProps.saving}
                                handleSubmitWithRedirect={formProps.handleSubmitWithRedirect}
                            />
                            <DeleteButton record={formProps.record} />
                        </Box>
                    </Toolbar>
                </>
            )
        }}
    />
}

export default MerchantCommon